import styles from './CurrentTariffsCard.module.scss'
import { Card, Heading, Toggle } from '@boltenergy-be/design-system'
import dayjs from 'dayjs'
import Link from 'components/link/Link.tsx'
import SocialTariffBanner from 'components/current-tariffs-card/components/social-tariff-banner/SocialTariffBanner.tsx'
import EnergyTypeOverview from './components/energy-type-overview/EnergyTypeOverview.tsx'
import DiscountCard from './components/discount-card/DiscountCard.tsx'
import { routes } from 'types/routes.ts'
import { useGetLanguage } from 'hooks/use-get-language'
import { UserTypes } from 'store/auth/types.ts'
import { selectPriceListsForSpecificServiceContract } from 'store/contact/selectors.ts'
import { useTranslation } from 'react-i18next'
import { ENERGY_TYPES, EnergyType, getProductFromPriceListUrl, PRODUCTS_CONFIG } from '@boltenergy-be/shared'
import NoConnectionCard from './components/no-connection-card/NoConnectionCard.tsx'
import useWindowSize from 'hooks/useWindowSize.tsx'
import { useState } from 'react'
import { CurrentTariffsCardProps } from './types.ts'
import NewTariffBanner from 'components/current-tariffs-card/components/new-tariff-banner/NewTariffBanner.tsx'

const CurrentTariffsCard = ({ electricity, gas, view }: CurrentTariffsCardProps) => {
  // Redux
  const priceListsBreakdownEl = selectPriceListsForSpecificServiceContract(electricity)
  const priceListsBreakdownGas = gas ? selectPriceListsForSpecificServiceContract(gas) : undefined

  // Custom hooks
  const { isTablet } = useWindowSize()

  // i18n
  const { t } = useTranslation(['tariffs', 'common'])
  const language = useGetLanguage()

  // Local state
  const [selectedEnergyType, setSelectedEnergyType] = useState<EnergyType>(ENERGY_TYPES.electricity)

  // Constants
  const isSuperUser = view === UserTypes.SUPER_USER

  // Constants - current
  const currentPriceList = priceListsBreakdownEl?.current
  const currentProduct = currentPriceList?.url ? getProductFromPriceListUrl(currentPriceList?.url) : undefined
  const selectedProductConfig = currentProduct ? PRODUCTS_CONFIG[currentProduct] : null

  // Constants - future
  const mostFuturePriceListElectricity = priceListsBreakdownEl?.future?.sort((a, b) => dayjs(a.fromDate).diff(dayjs(b.fromDate)))?.[0]
  const mostFuturePriceListGas = gas
    ? priceListsBreakdownGas?.future?.sort((a, b) => dayjs(a.fromDate).diff(dayjs(b.fromDate)))?.[0]
    : undefined
  const futureProduct = mostFuturePriceListElectricity?.url ? getProductFromPriceListUrl(mostFuturePriceListElectricity?.url) : undefined
  const futureProductConfig = futureProduct ? PRODUCTS_CONFIG[futureProduct] : null

  const productConfigToShow = selectedProductConfig || futureProductConfig

  return productConfigToShow ? (
    <Card as="section" data-view={view} className={styles['current-tariffs']}>
      <header className={styles.header}>
        <small className="weight-semibold">{t(($) => $.overview.currentTariff.label)}</small>
        <Heading as="h2" variant="h4" serif>
          {productConfigToShow.name[language].toLowerCase().startsWith('plenty')
            ? productConfigToShow.name[language]
            : `Bolt ${productConfigToShow.name[language]}`}
        </Heading>
      </header>

      <div className={styles.overview}>
        {isTablet && (
          <Toggle
            options={[
              {
                label: t(($) => $.electricity, {
                  ns: 'common'
                }),
                value: ENERGY_TYPES.electricity
              },
              {
                label: t(($) => $.gas, {
                  ns: 'common'
                }),
                value: ENERGY_TYPES.gas
              }
            ]}
            onClick={(value) => setSelectedEnergyType(value)}
            active={selectedEnergyType}
            isFullwidth
          />
        )}

        <div className={styles['energy-types']}>
          {/* ELECTRICITY */}
          {((isTablet && selectedEnergyType === ENERGY_TYPES.electricity) || !isTablet) && (
            <EnergyTypeOverview
              serviceContract={electricity}
              priceLists={priceListsBreakdownEl}
              contractualStartDate={electricity.detail.contractualStartDate}
            />
          )}

          {/* GAS */}
          {((isTablet && selectedEnergyType === ENERGY_TYPES.gas) || !isTablet) && (
            <>
              {gas ? (
                <EnergyTypeOverview serviceContract={gas} priceLists={priceListsBreakdownGas} />
              ) : !isSuperUser ? (
                <NoConnectionCard />
              ) : null}
            </>
          )}
        </div>

        {isSuperUser && <DiscountCard />}
      </div>

      {isSuperUser && (
        <Link representation="button" href={routes.TARIFFS_PRODUCTS} className={styles.action}>
          {t(($) => $.overview.currentTariff.action)}
        </Link>
      )}

      {!!selectedProductConfig && !!futureProductConfig && (
        <NewTariffBanner
          serviceContracts={{ electricity, gas }}
          priceLists={{ [ENERGY_TYPES.electricity]: mostFuturePriceListElectricity, [ENERGY_TYPES.gas]: mostFuturePriceListGas }}
          newProduct={futureProduct}
        />
      )}

      <SocialTariffBanner serviceContract={electricity} />
    </Card>
  ) : null
}

export default CurrentTariffsCard
